"use client";

import staticData from "@/public/data/hotel/detail.json";
import { Card } from "@/shared/components/common/Card";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import type { PhosphorIconName } from "@/shared/components/common/PhosphorIcon";
import useSessionStorage from "@/shared/hooks/useSessionStorage";
import type { StorePassengersSchema } from "@/shared/schemas/hotel/storePassengersSchema";
import type { MinPriceRoom } from "@/types/services/hotel/apis/Detail";
import RoomCardAction from "./components/RoomCardAction";
import RoomCardDescription from "./components/RoomCardDescription";
import RoomCardPrice from "./components/RoomCardPrice";
import RoomCardTag, { type RoomCardTagType } from "./components/RoomCardTag";
import RoomCardTitle from "./components/RoomCardTitle";

interface RoomCardProps {
  room: MinPriceRoom;
  stayNights: number;
}

export default function RoomCard({ room, stayNights }: RoomCardProps) {
  const { roomCard } = staticData;

  const [cachedFormData, setCachedFormData] =
    useSessionStorage<StorePassengersSchema | null>("HotelcachedPassengers");

  if (cachedFormData) setCachedFormData(null);

  const { name, rate_plans, capacity } = room;

  const roomTags: RoomCardTagType[] = [
    {
      status: rate_plans.reservation_state === "online" ? "active" : "inactive",
      title: roomCard.tags.reservationStateLabel,
      variant: "outline",
      icon: "Lightning",
    },
    {
      status:
        rate_plans.rules.child_rule.infant_count > 0 ? "active" : "inactive",
      title: roomCard.tags.infantLabel,
      variant: "outline",
      icon: "BabyCarriage",
    },
    {
      status:
        rate_plans.rules.child_rule.max_child_age > 0 ? "active" : "inactive",
      title: roomCard.tags.childLabel,
      variant: "outline",
      icon: "Baby",
    },
  ];

  const capacityIcons: Record<number, PhosphorIconName> = {
    1: "User",
    2: "Users",
    3: "UsersThree",
    4: "UsersFour",
  };

  return (
    <Card className="flex flex-col gap-8 ring-1 ring-ring border-none rounded-lg bg-background p-4 shadow-none">
      {/* Room Title & Description */}
      <Flex.Item className="w-full">
        <Flex
          direction={{
            default: "col",
            md: "row",
          }}
          alignItems={{
            default: "start",
            md: "center",
          }}
          justifyContent={"between"}
          gap={"3"}
        >
          <RoomCardTitle name={name} ratePlan={rate_plans.name} />

          <Flex alignItems={"center"} gap={"2"}>
            <List
              items={roomTags}
              renderItems={(tag) =>
                tag.status === "active" && <RoomCardTag tag={tag} />
              }
            />
          </Flex>
        </Flex>
      </Flex.Item>

      {/* Room Action & Tags */}
      <Flex.Item className="w-full">
        <Flex alignItems={"end"} justifyContent={"between"}>
          <Flex alignItems={"center"} gap={"3"} className="hidden sm:flex">
            <Flex alignItems={"center"} gap={"2"}>
              <RoomCardDescription
                title={`${capacity} ${roomCard.manLabel}`}
                icon={capacityIcons[capacity <= 4 ? capacity : 4]}
              />
              <RoomCardDescription
                title={`${stayNights} ${roomCard.nightCountLabel}`}
                icon={"Clock"}
              />
            </Flex>
          </Flex>

          <Flex
            direction={{
              default: "row",
              md: "col",
            }}
            justifyContent={{
              default: "between",
              md: "center",
            }}
            alignItems={"center"}
            gap={"1"}
            className="w-full sm:w-auto"
          >
            <RoomCardPrice
              price={room.rate_plans.price}
              currencyLabel={roomCard.currencyLabel}
            />
            <RoomCardAction
              label={roomCard.selectRoomLabel}
              limitLabel={roomCard.selectRoomLimitLabel}
              room={room}
            />
          </Flex>
        </Flex>
      </Flex.Item>
    </Card>
  );
}
